import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Card, BUTTON_TYPES, Button, Input, Checkbox, Select, notification, AtsSkeleton } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import history from 'helpers/history';
import { ACTIONS, Can, UI } from 'permission';
import { getJobs } from 'actions';
import { clearCandidatesJob, setCandidateExists } from 'store/reducers/jobReducer';
import { getJobLoader } from 'store/selectors/jobSelectors';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import img from 'dist/emptyStates/stepsNoCandidates.svg';
import { useAppDispatch } from 'store';
import type { CandidatesJobBody, CandidatesJobResponse } from 'actions/jobActions';
import type { CandidateJobAssignment } from 'store/reducers/candidateReducer';
import type { Uuid } from 'store/reducers/types';
import './styles.scss';

interface StepCandidateSummary {
  uuid: Uuid;
  first_name_latin?: string;
  last_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
}

interface StepCandidateAssignment {
  candidate: StepCandidateSummary;
  [key: string]: unknown;
}

interface CandidatesGroup {
  list: StepCandidateAssignment[];
  count: number;
  hasMore: boolean;
}

interface StatusOption {
  value: string;
  label: string;
}

interface StepsLeftPanelProps {
  candidates: Record<string, CandidatesGroup | undefined>;
  activeStepLabels: Array<{ name: string }>;
  getCandidatesJobs: (
    id: Uuid,
    stepId: Uuid,
    clear: boolean,
    body: CandidatesJobBody
  ) => Promise<CandidatesJobResponse | null | undefined>;
  candidateSearch?: string;
  onlyMyCandidates?: boolean;
  statuses: StatusOption[];
  updateParams: (field: string, value: unknown) => void;
  setActiveStep: (stepId: Uuid) => void;
}

const EmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="left-panel__empty">
      <img src={img} alt="" />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:steps_left_panel.text_no_statuses_selected')}
      </Typography.Title3>
    </div>
  );
};

export const StepsLeftPanel: React.FC<StepsLeftPanelProps> = ({
  candidates,
  activeStepLabels,
  getCandidatesJobs,
  candidateSearch,
  onlyMyCandidates,
  statuses,
  updateParams,
  setActiveStep,
}) => {
  const { t } = useTranslation();
  const { id, step, candidate } = useParams<{ id: string; step: string; candidate: string }>();
  const loader = useSelector(getJobLoader);
  const dispatch = useAppDispatch();
  const [candidateSelect, setCandidateSelect] = useState<{ value: Uuid; label: string } | undefined>();
  const [isRedirected, setIsRedirected] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const candidatesOptions = React.useMemo(() => {
    const options: Array<{ label: string; options: Array<{ value: Uuid; label: string }> }> = [];
    statuses.forEach(status => {
      const candidatesByStatus =
        candidates[status.value]?.list.map(c => ({
          value: c.candidate.uuid,
          label: `${c.candidate.first_name_latin} ${c.candidate.last_name_latin}`,
        })) || [];
      options.push({ label: `${status.label} (${candidates[status.value]?.count || 0})`, options: candidatesByStatus });
    });
    return options;
  }, [candidates, statuses]);

  useEffect(() => {
    candidatesOptions.forEach(o =>
      o.options.forEach(option => {
        if (option.value === candidate) {
          setCandidateSelect(option);
        }
      })
    );
  }, [candidatesOptions, candidate]);

  const onOpenFilters = () => setFiltersOpen(o => !o);

  const getNewCandidates = (status: string, isNew?: boolean, search = candidateSearch, redirect = true) =>
    getCandidatesJobs(id, step, isNew as boolean, {
      limit: 5,
      offset: isNew ? 0 : candidates[status]?.list.length,
      status,
      only_my_candidates: onlyMyCandidates,
      search,
      user_uuid: candidate,
    }).then(resp => {
      const candidateData =
        (resp?.results as unknown as StepCandidateAssignment[]).find(item => item.candidate.uuid === candidate)
          ?.candidate ?? (resp?.results as unknown as StepCandidateAssignment[])[0]?.candidate;
      if (resp && isNew && redirect && window.location.pathname.includes('show/steps')) {
        history.push(`/jobs/${id}/show/steps/${step}${candidateData?.uuid ? `/${candidateData.uuid}` : ''}`);
      }
      return resp?.results as unknown as StepCandidateAssignment[];
    });

  const onSearch = (s: string) => {
    history.push(`/jobs/${id}/show/steps/${step}`);
    dispatch(clearCandidatesJob());
    Promise.all(statuses.map(status => getNewCandidates(status.value, true, s, false))).then(resp => {
      const results = resp.flat();
      if (results.length) {
        const candidateData = results[0].candidate;
        history.push(`/jobs/${id}/show/steps/${step}${candidateData?.uuid ? `/${candidateData.uuid}` : ''}`);
      }
    });
  };

  useEffect(() => {
    if (step) {
      Promise.all(statuses.map(el => getNewCandidates(el.value, true, candidateSearch, false))).then(async resp => {
        const results = resp.flat();
        const candidateId = results.find(item => item.candidate.uuid === candidate)?.candidate.uuid;
        if (candidate && !candidateId && !isRedirected) {
          try {
            const jobs = await dispatch(getJobs(candidate));
            const { active_step, opening } = (jobs?.find(j => j.job_uuid === id) || {}) as CandidateJobAssignment & {
              opening?: { resolution?: string };
            };
            if (active_step) {
              setActiveStep(active_step.uuid);
              dispatch(setCandidateExists(true));
            } else if (opening && opening.resolution === 'Hired') {
              history.push(`/jobs/${id}/show/hired`);
            } else {
              notification({ message: "The candidate you are looking for isn't assigned to this job." });
            }
            setIsRedirected(true);
          } catch {
            notification({ message: "The candidate you are looking for isn't assigned to this job." });
          }
        } else if (results.length && window.location.pathname.includes('show/steps')) {
          const firstCandidateId = candidateId || results[0]?.candidate.uuid;
          history.push(`/jobs/${id}/show/steps/${step}${firstCandidateId ? `/${firstCandidateId}` : ''}`);
          dispatch(setCandidateExists(true));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyMyCandidates, step]);

  return (
    <Card className="left-panel">
      <div className={cn('left-panel__title', { open: filtersOpen })}>
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:steps_left_panel.title')}</Typography.Title2>
        <Button
          icon={<Arrow />}
          type={BUTTON_TYPES.GHOST}
          className={cn('original-svg', { open: filtersOpen })}
          onClick={onOpenFilters}
        />
      </div>
      <div className={cn('left-panel__filters', { open: filtersOpen })}>
        <Input
          className="left-panel__search"
          placeholder={t('translation:steps_left_panel.ph_search_by_name')}
          value={candidateSearch}
          itemProps={{
            onChange: e => updateParams('candidateSearch', e.target.value),
          }}
          maxLength={50}
          onDebounceChange={e => {
            onSearch(e.target.value);
            updateParams('candidateSearch', e.target.value);
          }}
          type="search"
        />
        <Can I={ACTIONS.READ} a={UI.ONLY_MU_CANDIDATES}>
          <Checkbox
            className="left-panel__checkbox"
            label={t('translation:steps_left_panel.label_only_my_candidates')}
            isSelected={onlyMyCandidates}
            onChange={e => {
              dispatch(clearCandidatesJob());
              history.push(`/jobs/${id}/show/steps/${step}`);
              updateParams('onlyMyCandidates', e.target.checked);
            }}
          />
        </Can>
        <Select
          label={t('translation:steps_left_panel.label_candidates')}
          placeholder={t('translation:steps_left_panel.ph_candidates')}
          className="left-panel__candidates"
          optionFilterProp="label"
          value={candidateSelect}
          onChange={(c: { value: Uuid }) => history.push(`/jobs/${id}/show/steps/${step}/${c.value}`)}
          options={candidatesOptions}
          // loading={Object.values(loader).find(l => !!l)}
        />
      </div>
      {statuses.length ? (
        activeStepLabels.map(({ name }) => (
          <div className={cn('status-block', { 'status-block_empty': !candidates[name]?.count })} key={name}>
            <Typography.Title3 className="status-block__title">
              {name}
              <span>({candidates[name]?.count || 0})</span>
            </Typography.Title3>
            {candidates[name]?.list.map(item => {
              const en_name = `${item.candidate.first_name_latin} ${item.candidate.last_name_latin}`;
              const ru_name = `${item.candidate.first_name_local} ${item.candidate.last_name_local}`;
              return (
                <NavLink
                  key={item.candidate.uuid}
                  className="left-panel_items"
                  to={`/jobs/${id}/show/steps/${step}/${item.candidate.uuid}`}
                  onClick={() => dispatch(setCandidateExists(true))}
                >
                  <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD} className="left-panel_items__title">
                    {en_name.trim() ? en_name : ru_name}
                  </Typography.Text>
                  {!!ru_name.trim() && !!en_name.trim() && (
                    <Typography.Caption className="left-panel_items__subtitle">{ru_name}</Typography.Caption>
                  )}
                </NavLink>
              );
            })}
            {loader[name] && <AtsSkeleton count={5} height={56} />}
            {candidates[name]?.hasMore && !loader[name] && (
              <Button onClick={() => getNewCandidates(name)} type={BUTTON_TYPES.TEXT} className="status-block__button">
                {t('translation:steps_left_panel.button_see_more')}
              </Button>
            )}
          </div>
        ))
      ) : (
        <EmptyState />
      )}
    </Card>
  );
};
